/**
 * PHASE 9 — search UI (autocomplete + results).
 *
 * A small floating panel over the sky. Typing runs the catalog's offline
 * search index (searchSky) — stars, deep-sky objects, constellations and
 * solar-system bodies — and shows a ranked, keyboard-navigable result list.
 * Selecting a result hands the object id to the caller, which starts
 * SeeIt-mode guidance.
 *
 * Pure DOM; all data comes from the catalog, no network.
 */

import { searchSky, type SearchResult } from "../catalog/catalog";

export const SEARCH_TYPE_LABELS: Readonly<Record<string, string>> = {
  STAR: "star",
  PLANET: "planet",
  MOON: "moon",
  SUN: "sun",
  CONSTELLATION: "constellation",
  GALAXY: "galaxy",
  NEBULA: "nebula",
  CLUSTER: "cluster",
  SATELLITE: "satellite",
};

export interface SearchPanelOptions {
  /** Debounce for autocomplete, ms. Default 180. */
  debounceMs?: number;
  /** Maximum result rows shown. Default 10. */
  maxResults?: number;
  onSelect?: (result: SearchResult) => void;
  onClose?: () => void;
}

export class SearchPanel {
  private input: HTMLInputElement;
  private listEl: HTMLElement;
  private closeBtn: HTMLElement;
  private results: SearchResult[] = [];
  private selectedIndex = -1;
  private debounceTimer: number | null = null;
  private debounceMs: number;
  private maxResults: number;
  private onSelect: ((r: SearchResult) => void) | undefined;
  private onClose: (() => void) | undefined;
  private documentHandler = (e: PointerEvent): void => {
    if (this.isOpen && !this.container.contains(e.target as Node)) this.close();
  };

  constructor(
    private container: HTMLElement,
    options: SearchPanelOptions = {},
  ) {
    this.debounceMs = options.debounceMs ?? 180;
    this.maxResults = options.maxResults ?? 10;
    this.onSelect = options.onSelect;
    this.onClose = options.onClose;
    this.input = container.querySelector<HTMLInputElement>(".search-input")!;
    this.listEl = container.querySelector<HTMLElement>(".search-results")!;
    this.closeBtn = container.querySelector<HTMLElement>(".search-close")!;

    this.input.addEventListener("input", () => this.scheduleSearch());
    this.input.addEventListener("keydown", (e) => this.onKey(e));
    this.closeBtn.addEventListener("click", () => this.close());
    this.container.addEventListener("pointerdown", (e) => e.stopPropagation());
  }

  get isOpen(): boolean {
    return this.container.classList.contains("open");
  }

  toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  open(): void {
    this.container.classList.add("open");
    this.input.focus();
    if (this.input.value.length >= 2) this.runSearch(this.input.value);
    document.addEventListener("pointerdown", this.documentHandler, true);
  }

  close(): void {
    if (!this.isOpen) return;
    this.container.classList.remove("open");
    this.listEl.replaceChildren();
    this.results = [];
    this.selectedIndex = -1;
    document.removeEventListener("pointerdown", this.documentHandler, true);
    this.onClose?.();
  }

  clear(): void {
    this.input.value = "";
    this.listEl.replaceChildren();
    this.results = [];
    this.selectedIndex = -1;
  }

  private scheduleSearch(): void {
    if (this.debounceTimer !== null) window.clearTimeout(this.debounceTimer);
    this.debounceTimer = window.setTimeout(() => {
      this.debounceTimer = null;
      this.runSearch(this.input.value);
    }, this.debounceMs);
  }

  private runSearch(query: string): void {
    if (query.trim().length < 2) {
      this.listEl.replaceChildren();
      this.results = [];
      this.selectedIndex = -1;
      return;
    }
    this.results = searchSky(query, this.maxResults);
    this.selectedIndex = this.results.length > 0 ? 0 : -1;
    this.render();
  }

  private render(): void {
    this.listEl.replaceChildren();
    if (this.results.length === 0) {
      const empty = document.createElement("div");
      empty.className = "search-empty";
      empty.textContent = "no matches";
      this.listEl.appendChild(empty);
      return;
    }
    this.results.forEach((r, i) => {
      const row = document.createElement("button");
      row.type = "button";
      row.className = "search-row";
      row.setAttribute("role", "option");
      row.setAttribute("aria-selected", String(i === this.selectedIndex));
      row.dataset.index = String(i);
      const name = document.createElement("span");
      name.className = "search-name";
      name.textContent = r.name;
      const type = document.createElement("span");
      type.className = "search-type";
      type.textContent = SEARCH_TYPE_LABELS[r.type] ?? r.type;
      row.append(name, type);
      row.addEventListener("pointerdown", (e) => {
        e.stopPropagation();
        this.select(i);
      });
      this.listEl.appendChild(row);
    });
    this.highlight(this.selectedIndex);
  }

  private highlight(index: number): void {
    const rows = this.listEl.querySelectorAll<HTMLElement>(".search-row");
    rows.forEach((row, i) => {
      row.classList.toggle("selected", i === index);
      row.setAttribute("aria-selected", String(i === index));
    });
  }

  private select(index: number): void {
    const result = this.results[index];
    if (!result) return;
    this.close();
    this.onSelect?.(result);
  }

  private onKey(e: KeyboardEvent): void {
    if (e.key === "Escape") {
      e.preventDefault();
      this.close();
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (this.selectedIndex >= 0) this.select(this.selectedIndex);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (this.results.length === 0) return;
      this.selectedIndex = (this.selectedIndex + 1) % this.results.length;
      this.highlight(this.selectedIndex);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (this.results.length === 0) return;
      this.selectedIndex = (this.selectedIndex - 1 + this.results.length) % this.results.length;
      this.highlight(this.selectedIndex);
    }
  }
}